const resources = {
  "en": {
    "rewardManager": {
      "title": "Rewards",
      "description": "Redeem points, review reward history, and manage promo codes or transferable certificates from one place.",
      "snapshot": {
        "title": "Rewards snapshot",
        "caption": "Start with your current balance, then decide whether to redeem, review, or share.",
        "promos": "Promo codes",
        "promosHelper": "Issued codes in this workspace",
        "certificates": "Certificates",
        "certificatesHelper": "Issued or shared annual certificates"
      },
      "balance": {
        "label": "Current balance",
        "subtext": "Total across all entries: {{total}} points"
      },
      "redeem": {
        "title": "Redeem rewards",
        "description": "Choose how to spend your points without leaving the rewards page.",
        "pointsLabel": "{{points}} pts",
        "unavailable": "You need {{points}} pts available to redeem this reward.",
        "invalidType": "Select a valid reward to redeem.",
        "insufficient": "Not enough points to redeem this reward.",
        "error": "Unable to process the redemption right now.",
        "success": {
          "subscription": "Subscription month added to your account.",
          "subscriptionWithCode": "Promo code issued: {{code}}",
          "gpt": "{{credits}} GPT credits added (balance: {{balance}}).",
          "certificate": "Certificate issued: {{code}}"
        },
        "options": {
          "subscription": {
            "title": "Monthly subscription credit",
            "description": "Convert points into a full month of access.",
            "detail": "Redeem and apply during your next billing cycle.",
            "detailWithCoupon": "Redeem to receive coupon {{coupon}} for your next billing cycle.",
            "cta": "Redeem month"
          },
          "gpt": {
            "title": "GPT credit bundle",
            "description": "Top up your AI analysis credits instantly.",
            "detail": "Includes {{credits}} credits you can use right away.",
            "cta": "Add credits"
          },
          "certificate": {
            "title": "Annual certificate",
            "description": "Unlock a transferable year-long membership certificate.",
            "detail": "Share with a teammate or friend when you are ready.",
            "cta": "Issue certificate"
          }
        }
      },
      "history": {
        "title": "Reward history",
        "description": "Recent reward entries are listed here with points, timestamps, and any certificate details.",
        "empty": "No rewards yet",
        "points": "{{points}} pts",
        "noDescription": "No description",
        "certificateCode": "Certificate code: {{code}}"
      },
      "promocodes": {
        "title": "Issued promo codes",
        "description": "Track promo codes you already issued and whether they were redeemed or expired.",
        "empty": "No promo codes issued yet",
        "issuedAt": "Issued: {{date}}",
        "expiresAt": "Expires: {{date}}",
        "redeemedAt": "Redeemed: {{date}}"
      },
      "certificates": {
        "title": "Certificates",
        "description": "Manage active annual certificates and send them to a recipient when you are ready.",
        "empty": "No certificates yet",
        "shareCta": "Share",
        "issuedAt": "Issued {{date}}",
        "pendingRecipient": "Shared with {{email}} (awaiting claim)",
        "lastSharedAt": "Last shared {{date}}",
        "status": {
          "active": "Active",
          "revoked": "Revoked",
          "redeemed": "Redeemed",
          "pending": "Pending",
          "cancelled": "Cancelled"
        },
        "shareModal": {
          "title": "Share certificate {{code}}",
          "submit": "Send invite",
          "recipientEmail": "Recipient email",
          "recipientName": "Recipient name (optional)",
          "message": "Message (optional)",
          "recipientPlaceholder": "friend@example.com",
          "recipientNamePlaceholder": "Who will receive this?",
          "messagePlaceholder": "Add a short note…",
          "validation": {
            "required": "Enter an email address",
            "email": "Enter a valid email address"
          },
          "success": "Certificate share updated.",
          "invalidRecipient": "Enter a valid recipient email.",
          "notFound": "Certificate could not be found.",
          "error": "Unable to update sharing details right now."
        }
      },
      "types": {
        "subscription": "Subscription",
        "gpt": "GPT credits",
        "certificate": "Certificate"
      },
      "status": {
        "available": "Available",
        "reserved": "Reserved",
        "redeemed": "Redeemed",
        "expired": "Expired",
        "inactive": "Inactive",
        "failed": "Failed",
        "unknown": "Unknown"
      },
      "errors": {
        "historyLoad": "Failed to load rewards",
        "promosLoad": "Failed to load promo codes"
      },
      "dates": {
        "unknown": "Unknown date"
      }
    }
  },
  "uk": {
    "rewardManager": {
      "title": "Нагороди",
      "description": "Обмінюйте бали, переглядайте історію нагород і керуйте промокодами або передаваними сертифікатами в одному місці.",
      "snapshot": {
        "title": "Зріз по нагородах",
        "caption": "Спочатку подивіться поточний баланс, а вже потім вирішуйте, що обміняти, перевірити або поширити.",
        "promos": "Промокоди",
        "promosHelper": "Видані коди в цьому робочому просторі",
        "certificates": "Сертифікати",
        "certificatesHelper": "Видані або поширені річні сертифікати"
      },
      "balance": {
        "label": "Поточний баланс",
        "subtext": "Разом по всіх записах: {{total}} балів"
      },
      "redeem": {
        "title": "Обміняти нагороди",
        "description": "Виберіть, як використати бали, не залишаючи сторінку нагород.",
        "pointsLabel": "{{points}} бал.",
        "unavailable": "Потрібно {{points}} балів, щоб обміняти цю нагороду.",
        "invalidType": "Виберіть дійсну нагороду для обміну.",
        "insufficient": "Недостатньо балів для обміну цієї нагороди.",
        "error": "Не вдалося зараз виконати обмін.",
        "success": {
          "subscription": "Місяць підписки додано до вашого облікового запису.",
          "subscriptionWithCode": "Промокод видано: {{code}}",
          "gpt": "{{credits}} GPT кредитів додано (баланс: {{balance}}).",
          "certificate": "Сертифікат видано: {{code}}"
        },
        "options": {
          "subscription": {
            "title": "Місячний кредит підписки",
            "description": "Обміняйте бали на повний місяць доступу.",
            "detail": "Обміняйте та застосуйте під час наступного платіжного циклу.",
            "detailWithCoupon": "Обміняйте, щоб отримати купон {{coupon}} для наступного платіжного циклу.",
            "cta": "Обміняти місяць"
          },
          "gpt": {
            "title": "Пакет GPT кредитів",
            "description": "Миттєво поповніть кредити для AI-аналізу.",
            "detail": "Включає {{credits}} кредитів, які можна використати одразу.",
            "cta": "Додати кредити"
          },
          "certificate": {
            "title": "Річний сертифікат",
            "description": "Відкрийте передавний річний сертифікат членства.",
            "detail": "Поділіться з колегою або другом, коли будете готові.",
            "cta": "Видати сертифікат"
          }
        }
      },
      "history": {
        "title": "Історія нагород",
        "description": "Тут відображаються останні записи нагород із балами, часом і деталями сертифікатів.",
        "empty": "Поки що немає нагород",
        "points": "{{points}} бал.",
        "noDescription": "Без опису",
        "certificateCode": "Код сертифіката: {{code}}"
      },
      "promocodes": {
        "title": "Видані промокоди",
        "description": "Перевіряйте вже видані промокоди та дивіться, чи були вони використані або втратили чинність.",
        "empty": "Поки що немає виданих промокодів",
        "issuedAt": "Видано: {{date}}",
        "expiresAt": "Діє до: {{date}}",
        "redeemedAt": "Використано: {{date}}"
      },
      "certificates": {
        "title": "Сертифікати",
        "description": "Керуйте активними річними сертифікатами та надсилайте їх отримувачу, коли будете готові.",
        "empty": "Поки що немає сертифікатів",
        "shareCta": "Поділитися",
        "issuedAt": "Видано {{date}}",
        "pendingRecipient": "Поділено з {{email}} (очікує підтвердження)",
        "lastSharedAt": "Останнє поширення {{date}}",
        "status": {
          "active": "Активний",
          "revoked": "Відкликано",
          "redeemed": "Погашено",
          "pending": "В очікуванні",
          "cancelled": "Скасовано"
        },
        "shareModal": {
          "title": "Поділитися сертифікатом {{code}}",
          "submit": "Надіслати запрошення",
          "recipientEmail": "Email отримувача",
          "recipientName": "Ім'я отримувача (необов'язково)",
          "message": "Повідомлення (необов'язково)",
          "recipientPlaceholder": "friend@example.com",
          "recipientNamePlaceholder": "Хто отримає це?",
          "messagePlaceholder": "Додайте коротку нотатку…",
          "validation": {
            "required": "Введіть адресу електронної пошти",
            "email": "Введіть коректну адресу електронної пошти"
          },
          "success": "Обмін сертифіката оновлено.",
          "invalidRecipient": "Введіть коректний email отримувача.",
          "notFound": "Сертифікат не знайдено.",
          "error": "Не вдалося оновити дані поширення."
        }
      },
      "types": {
        "subscription": "Підписка",
        "gpt": "GPT кредити",
        "certificate": "Сертифікат"
      },
      "status": {
        "available": "Доступний",
        "reserved": "Зарезервовано",
        "redeemed": "Погашено",
        "expired": "Термін дії минув",
        "inactive": "Неактивний",
        "failed": "Помилка",
        "unknown": "Невідомо"
      },
      "errors": {
        "historyLoad": "Не вдалося завантажити нагороди",
        "promosLoad": "Не вдалося завантажити промокоди"
      },
      "dates": {
        "unknown": "Невідома дата"
      }
    }
  }
};

export default resources;
